import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Briefcase, Plus, Users, FileText, Pause, Play, CheckCircle2, X, MapPin, Car, IdCard, Clock, Loader2, Sparkles, Star, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CONTRACTS = [["stagionale", "Stagionale"], ["part_time", "Part-time"], ["full_time", "Full-time"], ["altro", "Altro"]];
const JOB_STATUS = { active: "Attivo", paused: "Sospeso", closed: "Chiuso", filled: "Occupato", draft: "Bozza" };
const APP_ACTIONS = [["viewed", "Vista"], ["shortlisted", "In valutazione"], ["contacted", "Contattato"], ["hired", "Assumi"], ["rejected", "Rifiuta"]];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobsPanel() {
  const [roleTypes, setRoleTypes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const load = () => api.get("/jobs/mine").then(({ data }) => setJobs(data));
  useEffect(() => { api.get("/jobs/meta").then(({ data }) => setRoleTypes(data.role_types)); load(); }, []);
  const roleLabel = (k) => roleTypes.find((r) => r.key === k)?.label || k;

  const setStatus = async (id, status) => { await api.patch(`/jobs/${id}/status`, { status }); load(); };

  return (
    <div className="space-y-5" data-testid="business-jobs-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#00E676]/15 border border-[#00E676]/30 flex items-center justify-center"><Briefcase className="h-5 w-5 text-[#00E676]" /></div>
          <div><h2 className="font-display text-xl font-bold">Lavoro · Annunci</h2><p className="text-sm text-zinc-400">Pubblica offerte e trova personale nel territorio.</p></div>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-[#00E676] text-black" data-testid="job-new-btn"><Plus className="h-4 w-4 mr-1" /> Nuovo annuncio</Button>
      </div>

      {jobs.length === 0 && <div className="text-sm text-zinc-500 text-center py-10 border border-dashed border-white/10 rounded-xl">Nessun annuncio pubblicato.</div>}
      <div className="grid gap-3">
        {jobs.map((j) => (
          <div key={j.id} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4" data-testid={`job-card-${j.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold flex items-center gap-2">{j.title}
                  <Badge className="bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30">{roleLabel(j.role_type)}</Badge>
                  <Badge variant="secondary">{JOB_STATUS[j.status]}</Badge>
                </div>
                <div className="text-sm text-zinc-400 mt-1">{CONTRACTS.find((c) => c[0] === j.contract_type)?.[1]} · {Math.max(0, j.positions - j.positions_filled)}/{j.positions} posti · {j.applications_count || 0} candidature{j.new_applications_count > 0 ? ` (${j.new_applications_count} nuove)` : ""}</div>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" variant="secondary" onClick={() => setViewing(j)} data-testid={`job-applicants-${j.id}`}><Users className="h-4 w-4 mr-1" /> Candidati</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(j); setShowForm(true); }} data-testid={`job-edit-${j.id}`}>Modifica</Button>
                {j.status === "active"
                  ? <Button size="sm" variant="ghost" onClick={() => setStatus(j.id, "paused")} data-testid={`job-pause-${j.id}`}><Pause className="h-4 w-4" /></Button>
                  : <Button size="sm" variant="ghost" onClick={() => setStatus(j.id, "active")} data-testid={`job-resume-${j.id}`}><Play className="h-4 w-4" /></Button>}
                <Button size="sm" variant="ghost" onClick={() => setStatus(j.id, "filled")} title="Posto occupato" data-testid={`job-fill-${j.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && <JobForm roleTypes={roleTypes} job={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {viewing && <Applicants job={viewing} roleLabel={roleLabel} onClose={() => { setViewing(null); load(); }} />}
    </div>
  );
}

function JobForm({ roleTypes, job, onClose, onSaved }) {
  const [f, setF] = useState(job ? { ...job, requirements: (job.requirements || []).join("\n") } : { title: "", role_type: "cameriere", contract_type: "full_time", description: "", requirements: "", hours: "", positions: 1, salary: "", salary_visible: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    const payload = { ...f, requirements: f.requirements.split("\n").map((s) => s.trim()).filter(Boolean), positions: parseInt(f.positions || 1) };
    try {
      if (job) await api.put(`/jobs/${job.id}`, payload); else await api.post("/jobs", payload);
      toast.success(job ? "Annuncio aggiornato" : "Annuncio pubblicato"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141417] border border-white/10 rounded-2xl max-w-lg w-full max-h-[88vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="job-form">
        <div className="font-display text-lg font-bold">{job ? "Modifica annuncio" : "Nuovo annuncio"}</div>
        <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Titolo (es. Pizzaiolo serale)" className="bg-[#0A0A0C] border-white/10" data-testid="job-title" />
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-zinc-400">Ruolo</label>
            <Select value={f.role_type} onValueChange={(v) => set("role_type", v)}><SelectTrigger className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="job-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roleTypes.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select></div>
          <div><label className="text-xs text-zinc-400">Contratto</label>
            <Select value={f.contract_type} onValueChange={(v) => set("contract_type", v)}><SelectTrigger className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="job-contract"><SelectValue /></SelectTrigger>
              <SelectContent>{CONTRACTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Descrizione" className="bg-[#0A0A0C] border-white/10" data-testid="job-description" />
        <Textarea rows={2} value={f.requirements} onChange={(e) => set("requirements", e.target.value)} placeholder="Requisiti (uno per riga)" className="bg-[#0A0A0C] border-white/10" data-testid="job-requirements" />
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-xs text-zinc-400">Orari</label><Input value={f.hours} onChange={(e) => set("hours", e.target.value)} placeholder="18-24" className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="job-hours" /></div>
          <div><label className="text-xs text-zinc-400">Posti</label><Input type="number" value={f.positions} onChange={(e) => set("positions", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="job-positions" /></div>
          <div><label className="text-xs text-zinc-400">Retribuzione</label><Input value={f.salary} onChange={(e) => set("salary", e.target.value)} placeholder="facolt." className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="job-salary" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.salary_visible} onChange={(e) => set("salary_visible", e.target.checked)} data-testid="job-salary-visible" /> Mostra retribuzione ai candidati</label>
        <div className="flex gap-2 pt-1"><Button variant="secondary" onClick={onClose} className="flex-1">Annulla</Button><Button onClick={save} disabled={busy} className="flex-1 bg-[#00E676] text-black" data-testid="job-save">{busy ? "…" : "Salva"}</Button></div>
      </div>
    </div>
  );
}

function Applicants({ job, roleLabel, onClose }) {
  const [apps, setApps] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const load = () => api.get(`/jobs/${job.id}/applications`).then(({ data }) => setApps(data));
  useEffect(() => { load(); }, [job.id]);
  const setStatus = async (id, status) => { await api.post(`/jobs/applications/${id}/status`, { status }); load(); toast.success("Stato aggiornato"); };
  const runAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/ai-rank`);
      setApps(data.ranking || []);
      toast.success("Candidati ordinati con l'AI");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI non disponibile ora"); }
    finally { setAiBusy(false); }
  };
  const viewCv = async (candidateId) => {
    try {
      const res = await api.get(`/jobs/cv/${candidateId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "CV non disponibile"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141417] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="job-applicants-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-bold">Candidati · {job.title}</div>
          <button onClick={onClose}><X className="h-5 w-5 text-zinc-400" /></button>
        </div>
        {apps && apps.length > 0 && (
          <div className="flex items-center justify-between gap-3 mb-3 rounded-xl bg-[#00E676]/8 border border-[#00E676]/25 p-3 flex-wrap">
            <div className="text-xs text-zinc-300 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#00E676]" /> Fai analizzare i CV e ordinare i candidati dall'AI (il punteggio base resta).</div>
            <Button size="sm" onClick={runAi} disabled={aiBusy} className="bg-[#00E676] text-black" data-testid="ai-rank-btn">
              {aiBusy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analisi…</> : <><Sparkles className="h-4 w-4 mr-1" /> Ordina con AI</>}
            </Button>
          </div>
        )}
        {!apps && <div className="text-zinc-400"><Loader2 className="h-4 w-4 animate-spin inline" /> Caricamento…</div>}
        {apps && apps.length === 0 && <div className="text-sm text-zinc-500 text-center py-8">Ancora nessun candidato.</div>}
        <div className="space-y-3">
          {apps?.map((a) => {
            const s = a.candidate_summary || {};
            return (
              <div key={a.id} className={`bg-[#1C1C1F] border rounded-xl p-4 ${a.ai_top_pick ? "border-[#00E676]/60" : "border-white/10"}`} data-testid={`applicant-${a.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold flex items-center gap-2">{s.name || "Candidato"}
                      {a.ai_top_pick && <Badge className="bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/40" data-testid={`ai-top-${a.id}`}><Star className="h-3 w-3 mr-1" /> Consigliato AI</Badge>}
                    </div>
                    <div className="text-sm text-zinc-400">{s.headline}</div>
                    <div className="flex gap-2 mt-1 text-xs text-zinc-400 flex-wrap">
                      {s.zone && <span><MapPin className="inline h-3 w-3" /> {s.zone}</span>}
                      {s.immediate && <span><Clock className="inline h-3 w-3" /> Immediato</span>}
                      {s.has_license && <span><IdCard className="inline h-3 w-3" /> Patente</span>}
                      {s.has_vehicle && <span><Car className="inline h-3 w-3" /> Auto</span>}
                    </div>
                  </div>
                  <div className="text-right flex items-start gap-3">
                    <div>
                      <div className="text-2xl font-bold text-[#00E676]" data-testid={`applicant-score-${a.id}`}>{a.match_score}%</div>
                      <div className="text-[10px] text-zinc-500 uppercase">base</div>
                    </div>
                    {typeof a.ai_score === "number" && a.ai_generated_at && (
                      <div>
                        <div className="text-2xl font-bold text-[#7DF9FF]" data-testid={`applicant-ai-score-${a.id}`}>{a.ai_score}%</div>
                        <div className="text-[10px] text-zinc-500 uppercase flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI</div>
                      </div>
                    )}
                  </div>
                </div>
                {a.ai_motivation && (
                  <div className="mt-3 rounded-lg bg-[#0A0A0C] border border-[#7DF9FF]/20 p-3" data-testid={`applicant-ai-${a.id}`}>
                    <div className="text-xs text-[#7DF9FF] font-semibold flex items-center gap-1.5 mb-1"><Sparkles className="h-3.5 w-3.5" /> Analisi AI</div>
                    <div className="text-xs text-zinc-300 leading-relaxed">{a.ai_motivation}</div>
                    {(a.ai_strengths || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {a.ai_strengths.map((x, i) => <span key={i} className="text-[11px] rounded-full bg-[#00E676]/12 text-[#00E676] border border-[#00E676]/30 px-2 py-0.5">+ {x}</span>)}
                      </div>
                    )}
                    {(a.ai_concerns || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {a.ai_concerns.map((x, i) => <span key={i} className="text-[11px] rounded-full bg-amber-500/12 text-amber-300 border border-amber-500/30 px-2 py-0.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {x}</span>)}
                      </div>
                    )}
                  </div>
                )}
                {/* Trasparenza: motivazione punteggio deterministico */}
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5" data-testid={`applicant-reasons-${a.id}`}>
                  {(a.match_reasons || []).map((r, i) => (
                    <div key={i} className="text-xs flex items-center gap-1.5">
                      {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-[#00E676] shrink-0" /> : <X className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                      <span className={r.ok ? "text-zinc-300" : "text-zinc-500"}>{r.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-1.5 mt-3 flex-wrap">
                  <Button size="sm" variant="secondary" onClick={() => viewCv(a.candidate_id)} data-testid={`applicant-cv-${a.id}`}><FileText className="h-4 w-4 mr-1" /> CV</Button>
                  {APP_ACTIONS.map(([st, l]) => <Button key={st} size="sm" variant="ghost" onClick={() => setStatus(a.id, st)} data-testid={`applicant-${st}-${a.id}`}>{l}</Button>)}
                  <Badge className="ml-auto" variant="secondary">{a.status}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
